// @flow
import React, { Fragment, } from 'react';
import { FelaTheme, } from 'react-fela';

import { AdSlot, SeoData, SortableTable, } from '@haaretz/htz-components';
import type { Node, } from 'react';

import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const numToString: number => string = num => num.toLocaleString('he', { minimumFractionDigits: 2, maximumFractionDigits: 2, });

function indices({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="מדדי מניות - TheMarker Finance"
        description="כל המידע על  מדדים: נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance"
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <Fragment>
            <PageRow>
              <RowItem title="מדדים">
                <SortableTable
                  assetsId={[
                    '2',
                    '142',
                    '137',
                    '164',
                    '143',
                    '167',
                    '145',
                    '149',
                    '30.10.!DJI',
                    '29.10.@CCO',
                    '33.10.!SPX',
                  ]}
                  miscStyles={{ marginTop: '2rem', tableLayout: 'auto', }}
                  disableSort
                  type="indices"
                  fragment="
                    name
                    value
                    changePercentage
                    weeklyYield
                    monthlyYield
                    quarterlyYield
                    yearlyYield
                    threeYearsYield
                  "
                  fields={[
                    {
                      name: 'name',
                      display: 'שם נייר',
                      sortingOrder: 'asc',
                      style: () => ({
                        fontWeight: '700',
                        maxWidth: '17rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                      value: ({ name, }) => name,
                    },
                    {
                      name: 'value',
                      display: 'שער אחרון',
                      sortingOrder: 'desc',
                      value: ({ value, }) => numToString(value || ''),
                    },
                    {
                      name: 'changePercentage',
                      display: '% שינוי',
                      sortingOrder: 'desc',
                      style: ({ changePercentage, }) => ({
                        ...(changePercentage
                          ? {
                            color:
                                changePercentage < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ changePercentage, }) => `
                        ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                        ${
                          changePercentage
                            ? numToString(Math.abs(changePercentage))
                            : changePercentage
                        }%
                       `,
                    },
                    {
                      name: 'weeklyYield',
                      display: 'תשואה שבועית',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ weeklyYield, }) => ({
                        ...(weeklyYield
                          ? {
                            color:
                                weeklyYield < 0 ? theme.color('negative') : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ weeklyYield, }) => `
                        ${weeklyYield > 0 ? '' : weeklyYield < 0 ? '-' : ''}
                        ${weeklyYield ? numToString(Math.abs(weeklyYield)) : weeklyYield}%
                       `,
                    },
                    {
                      name: 'monthlyYield',
                      display: 'תשואה חודשית',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ monthlyYield, }) => ({
                        ...(monthlyYield
                          ? {
                            color:
                                monthlyYield < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ monthlyYield, }) => `
                        ${monthlyYield > 0 ? '' : monthlyYield < 0 ? '-' : ''}
                        ${monthlyYield ? numToString(Math.abs(monthlyYield)) : monthlyYield}%
                       `,
                    },
                    {
                      name: 'quarterlyYield',
                      display: 'תשואה רבעונית',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ quarterlyYield, }) => ({
                        ...(quarterlyYield
                          ? {
                            color:
                                quarterlyYield < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ quarterlyYield, }) => `
                        ${quarterlyYield > 0 ? '' : quarterlyYield < 0 ? '-' : ''}
                        ${quarterlyYield ? numToString(Math.abs(quarterlyYield)) : quarterlyYield}%
                       `,
                    },
                    {
                      name: 'yearlyYield',
                      display: 'תשואה שנתית',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ yearlyYield, }) => ({
                        ...(yearlyYield
                          ? {
                            color:
                                yearlyYield < 0 ? theme.color('negative') : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ yearlyYield, }) => `
                        ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                        ${yearlyYield ? numToString(Math.abs(yearlyYield)) : yearlyYield}%
                       `,
                    },
                    {
                      name: 'threeYearsYield',
                      display: 'תשואה 3 שנים',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ threeYearsYield, }) => ({
                        ...(threeYearsYield
                          ? {
                            color:
                                threeYearsYield < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                          }
                          : {}),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ threeYearsYield, }) => `
                        ${threeYearsYield > 0 ? '' : threeYearsYield < 0 ? '-' : ''}
                        ${
                          threeYearsYield ? numToString(Math.abs(threeYearsYield)) : threeYearsYield
                        }%
                       `,
                    },
                  ]}
                  initialSort="name"
                  count={11}
                />
              </RowItem>
            </PageRow>
            <PageRow>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
          </Fragment>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default indices;
